import { useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from "react-i18next";
import { Button } from "@nextui-org/react";
import Header from "./Header";

function CodeValidator({ validate, target, sendCode, goback, validationErr, title }) {
    const { t } = useTranslation();
    const [code, setCode] = useState(new Array(6).fill(''));
    const inputRefs = useRef([]);
    const [timeLeft, setTimeLeft] = useState(300);
    const [expired, setExpired] = useState(false);
    const expiryRef = useRef(Date.now() + 300 * 1000);

    const handleChange = (element, index) => {
        if (isNaN(element.value)) return;
        const updated = [...code.map((d, idx) => (idx === index ? element.value : d))];
        setCode(updated);
        if (element.value !== '' && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleKeyUp = (event, index) => {
        if (event.key === 'Backspace' && index > 0 && code[index] === '') {
            inputRefs.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (!pasted) return;
        const updated = new Array(6).fill('').map((_, i) => pasted[i] || '');
        setCode(updated);
        const lastIndex = Math.min(pasted.length - 1, 5);
        inputRefs.current[lastIndex]?.focus();
    };

    const handleSubmit = () => {
        validate(code.join(''));
    };

    useEffect(() => {
        const tick = () => {
            const remaining = Math.max(0, Math.round((expiryRef.current - Date.now()) / 1000));
            setTimeLeft(remaining);
            if (remaining <= 0) setExpired(true);
        };

        const timer = setInterval(tick, 1000);

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') tick();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const isFilled = code.every(d => d !== '');

    const handleResend = () => {
        expiryRef.current = Date.now() + 300 * 1000;
        setTimeLeft(300);
        setExpired(false);
        setCode(new Array(6).fill(''));
        sendCode();
    };

    return (
        <div className="flex h-full flex-col">
            <Header goBack={goback} />
            <div className="flex flex-col p-5 flex-1 overflow-y-auto gap-8">

                {/* Heading */}
                <div className="flex flex-col items-center text-center gap-1">
                    <div className="heading text-xl">{title}</div>
                    <div className="desc text-sm">
                        <Trans
                            i18nKey="common.codeValidator.sentTo"
                            values={{ target }}
                            components={{ strong: <span className="font-medium" /> }}
                        />
                    </div>
                </div>

                {/* OTP inputs */}
                <div className="flex flex-col items-center gap-3">
                    <div className="flex gap-2">
                        {code.map((value, index) => (
                            <input
                                key={index}
                                ref={(el) => (inputRefs.current[index] = el)}
                                value={value}
                                onChange={(e) => handleChange(e.target, index)}
                                onKeyUp={(e) => handleKeyUp(e, index)}
                                onPaste={handlePaste}
                                maxLength={1}
                                inputMode="numeric"
                                style={{ fontSize: '16px' }}
                                onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                                className={`w-11 h-14 text-center text-lg font-semibold border-2 rounded-xl outline-none transition-colors otp-input ${
                                    value ? 'icon-badge-bg' : 'border-gray-200 bg-white'
                                }`}
                            />
                        ))}
                    </div>
                    {validationErr && (
                        <div className="text-red-500 text-sm text-center">{validationErr}</div>
                    )}
                    {!expired ? (
                        <div className="desc text-xs">
                            <Trans
                                i18nKey="common.codeValidator.expiresIn"
                                values={{ time: formatTime(timeLeft) }}
                                components={{ strong: <span className="font-semibold" /> }}
                            />
                        </div>
                    ) : (
                        <div className="text-red-500 text-xs">{t("common.codeValidator.expired")}</div>
                    )}
                </div>

                {/* CTA */}
                <div className="flex flex-col gap-2 mt-auto">
                    {/* Gated on `expired` — and routed through handleResend, which resets
                        the input boxes and restarts the countdown — rather than calling
                        sendCode directly, which would leave stale digits and a stale timer
                        in place after a resend.
                        variant="flat" is a deliberately lighter/secondary look next to the
                        solid Verify button below — no background override here, since
                        forcing a solid fill would erase that visual hierarchy between the
                        two actions. */}
                    <Button
                        radius="sm"
                        onClick={handleResend}
                        variant="flat"
                        color="primary"
                        fullWidth
                        isDisabled={!expired}
                    >
                        {t("common.codeValidator.resend")}
                    </Button>
                    <Button
                        radius="sm"
                        onClick={handleSubmit}
                        color="primary"
                        size="lg"
                        fullWidth
                        isDisabled={!isFilled || expired}
                    >
                        {t("common.codeValidator.verify")}
                    </Button>
                </div>

            </div>
        </div>
    );
}

export default CodeValidator;
